import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import { Paper, Typography, Grid } from "@mui/material";
// cmps
import GroupCategoriesFilter from "./group-categories-filter";
import GroupData from "./group-data";
import GroupGateways from "./group-gateways";
// hooks
import useSend from "../../hooks/useSend";
// services
import User from "../../services/user";
// types
import type { CategoryName, FilteredGroups, Group, GroupName } from "../../types/firmware-groups";
import type { CmdGetGroups } from "../../types/gupport";

const groupByCategory = (groupData: ReadonlyArray<Group>): FilteredGroups => {
	const grouped = Object.groupBy(groupData, ({ category }) => category);
	return Object.entries(grouped).map(([category, items]) => ({
		category: category as CategoryName,
		names: (items?.map(({ name }) => (name)) ?? []),
	}));
};

const FirmwareGroups = () => {
	const send = useSend();

	const { t } = useTranslation();

	const [groups, setGroups] = useState<FilteredGroups>([]);
	const [loading, setLoading] = useState(true);
	const [selectedGroupName, setSelectedGroupName] = useState<GroupName | undefined>(undefined);
	const [showGenericErrorMsg, setShowGenericErrorMsg] = useState<number | undefined>(undefined);

	const fetchGroups = useCallback(() => {
		setLoading(true);
		const cmd = {
			action: "getGroups",
			category: true,
		} as const satisfies CmdGetGroups;
		send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				setShowGenericErrorMsg(undefined);
				setGroups(groupByCategory(msg.payload.data));
			} else {
				setShowGenericErrorMsg(Date.now());
			}
			setLoading(false);
		});
	}, [send]);

	useEffect(() => {
		fetchGroups();
	}, [fetchGroups]);

	if (!User.hasLevel("gw_manage")) {
		return <Navigate to="./.." />;
	}

	return (
		<Grid container={true} spacing={2} wrap="nowrap">
			<Grid sx={{ width: 300, flexShrink: 0 }}>
				<GroupCategoriesFilter
					selectedGroupName={selectedGroupName}
					onGroupClick={setSelectedGroupName}
					groups={groups}
					loading={loading}
					fetchGroups={fetchGroups}
					showGenericErrorMsg={showGenericErrorMsg}
					setShowGenericErrorMsg={setShowGenericErrorMsg}
				/>
			</Grid>
			<Grid size={{ xs: 11 }}>
				{selectedGroupName ?
					<Paper sx={{ p: 2 }}>
						<Typography variant="h4">
							{t("firmwareGroups.groupName", { GROUP_NAME: selectedGroupName })}
						</Typography>
						<GroupData selectedGroupName={selectedGroupName} fetchGroups={fetchGroups} groups={groups} />
						<GroupGateways selectedGroupName={selectedGroupName} groups={groups} />
					</Paper>
					:
					<Typography sx={{ padding: "1em", textAlign: "center" }}>
						{t("firmwareGroups.selectGroup")}
					</Typography>
				}
			</Grid>
		</Grid>
	);
};

export default FirmwareGroups;
